import { useEffect, useRef, useState } from 'react';
import { PrismButton } from "@prism-ui/react";
import { ImageBox } from './FileImage';
import './FileInput.css';


const defaultInputTypes = ['jpeg', 'jpg', 'png'];

interface FileInputProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    object: any,
    /** This should correspond to a property on `object` that contains a reference to the file */
    fileKey: string,
    /** The text to display on the button */
    entityType: string,
    /** A list of allowed file types */
    allowedTypes: Array<string>,
    /** The shape of the image to use. Can be square, circle, or an empty string */
    imageVariant: string,
    /** Callback used when an invalid file type is selected */
    invalidFileTypeCallback: (invalidFileTypeSelected: boolean) => void
}

export const FileInput = ({ object, fileKey, entityType, invalidFileTypeCallback, imageVariant = 'square', allowedTypes = defaultInputTypes }: FileInputProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [entity, setEntity] = useState<any>(object);
    const [entityFileKey, setEntityFilekey] = useState<string>(fileKey);
    const [fileSelected, setFileSelected] = useState<boolean>(false);
    const [invalidTypeSelected, setInvalidTypeSelected] = useState<boolean>(false);
    const [acceptedInputs] = useState<Array<string>>(allowedTypes);
    const fileDivRef = useRef<HTMLInputElement | null>(null);


    useEffect(() => {

        if (object[fileKey] === undefined) {
            throw new Error(`File key not found in object`);
        }
        const fileSelected = object[fileKey] !== "" && object[fileKey] !== null;

        setFileSelected(fileSelected);
        setEntity(object);
        setEntityFilekey(fileKey);
    }, [object, fileKey, entityType])

    const displayFilePrompt = () => {
        fileDivRef?.current?.click();
    }

    const removeImage = () => {
        if (fileSelected) {
            entity[entityFileKey] = "";
            setEntity(entity);
            setInvalidTypeSelected(false)
            setFileSelected(false);
        }
    }

    const validateMimeType = (mimeType: string): boolean => {
        let isInvalid = false;
        let validTypeFound = false;

        mimeType = mimeType.toLowerCase().trim();

        if (mimeType === "") {  // BUG FIX: Use strict equality
            isInvalid = true;
            return isInvalid;
        }

        for (const acceptedType of allowedTypes) {
            if (mimeType.indexOf(acceptedType) > -1) {
                validTypeFound = true;
            }
        }

        if (!validTypeFound) {
            isInvalid = true;
        }

        return isInvalid;
    }

    const renderInput = () => {
        const fileSelected = entity[fileKey] !== "" && entity[fileKey] !== null;

        if (fileSelected) {
            const prettyImageName = (): string => {
                if (typeof entity[fileKey] === 'string' || entity[fileKey] instanceof String) {
                    // Extract filename from path, handling edge cases
                    const filepath = String(entity[fileKey]);
                    const filenameStart = filepath.lastIndexOf("/");
                    // If no "/" found, return entire string; otherwise return substring after last "/"
                    return filenameStart === -1 ? filepath : filepath.slice(filenameStart + 1);
                } else {
                    return entity[fileKey].name || 'Unknown file';
                }
            };

            return (
                <>
                    <div className="file-input-group">
                        <PrismButton name="logo" onPrismClick={displayFilePrompt}>Update {entityType}</PrismButton>
                        <input
                            type="text"
                            className={`file-input-filename${invalidTypeSelected ? ' file-input-filename--invalid' : ''}`}
                            placeholder={prettyImageName()}
                            onMouseUp={displayFilePrompt}
                            aria-label="Update Logo"
                            aria-describedby="logo"
                            aria-invalid={invalidTypeSelected || undefined}
                            readOnly
                        />
                        <PrismButton
                            tone="danger"
                            onPrismClick={() => { removeImage() }}
                            disabled={!fileSelected}
                        >
                            Remove {entityType}
                        </PrismButton>
                    </div>
                    {invalidTypeSelected && (
                        <p className="file-input-feedback" role="alert">
                            Invalid file format - please select one of the following formats: {acceptedInputs.join(' or ')}.
                        </p>
                    )}
                </>
            );
        }

        return (
            <div className="file-input-group">
                <PrismButton id="project-logo" onPrismClick={displayFilePrompt}>Select {entityType}</PrismButton>
                <input
                    type="text"
                    className="file-input-filename"
                    onMouseUp={displayFilePrompt}
                    readOnly
                    placeholder={`Select a ${acceptedInputs.join(' or ')}. Note: Images will be resized to 200x200px`}
                    aria-label="Logo"
                    aria-describedby="project-logo"
                />
            </div>
        );
    }

    const toBase64 = async (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                resolve(reader.result);
            }
            reader.onerror = reject;
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = async (e: any) => {
        if (e.target.name === entityFileKey && e.target.files.length > 0) {  // BUG FIX: Use strict equality
            const file = e.target.files[0];
            const { type: mimetype, name } = file;
            const invalidTypeSelected = validateMimeType(mimetype);

            await toBase64(file).then((res) => {
                const b64Data = res;
                entity[entityFileKey] = {
                    name,
                    data: b64Data,
                    mimetype,
                    file
                }
                setFileSelected(true);
                invalidFileTypeCallback(invalidTypeSelected);
                setEntity(entity);
            }).catch((err) => {
                console.log(err);
            });
        }
    }

    const fileInput = renderInput();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allowedInputTypes = acceptedInputs.map((t: any) => {
        return `.${t}`;
    }).join();
    const imageURL = entity[entityFileKey] != null ? entity[entityFileKey].file || entity[entityFileKey] : '';
    return (

        <div className="form-group file-input">
            <label className="form-group-label">{entityType}</label>
            {entity[entityFileKey] != null && entity[entityFileKey] != '' && !invalidTypeSelected ? (
                <div className="form-group">
                    <ImageBox imageURL={imageURL} imageVariant={imageVariant} />
                </div>
            ) : ''}
            {fileInput}
            <input
                type="file"
                name={fileKey}
                ref={fileDivRef}
                style={{ display: "none" }}
                accept={allowedInputTypes}
                onChange={handleChange}
            />
        </div>


    )
}